import { AlertTriangle, Info, UserPlus } from 'lucide-react';
import type { ResultsPlan } from '../../../import/resultPlan';

/**
 * What the file says, before anything is written.
 *
 * The point of the whole wizard: results are replaced wholesale, so an import is not an addition
 * to what the tournament holds — it *is* what the tournament holds afterwards. That is stated
 * before the table rather than discovered after it.
 *
 * One error anywhere blocks the run, so a plan with errors shows no rows to approve. The errors
 * are the review in that case.
 */
export function ReviewResultsStep({ plan }: { plan: ResultsPlan }) {
  const errors = plan.issues.filter((i) => i.level === 'error');
  const warnings = plan.issues.filter((i) => i.level === 'warning');

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-800/70">
        <span>
          <strong className="text-ink-950">{plan.dataRows}</strong> rows read
        </span>
        {plan.results.length > 0 ? (
          <span>
            <strong className="text-ink-950">
              {plan.results.filter((r) => r.place !== null).length}
            </strong>{' '}
            paid,{' '}
            <strong className="text-ink-950">
              {plan.results.filter((r) => r.place === null).length}
            </strong>{' '}
            entered without placing
          </span>
        ) : null}
        {plan.results.some((r) => r.unclaimed !== null) ? (
          <span>
            <strong className="text-ink-950">
              {plan.results.filter((r) => r.unclaimed !== null).length}
            </strong>{' '}
            unclaimed {plan.results.filter((r) => r.unclaimed !== null).length === 1 ? 'place' : 'places'}
          </span>
        ) : null}
        {plan.blankRows > 0 ? <span>{plan.blankRows} blank skipped</span> : null}
        {plan.newPlayers.length > 0 ? (
          <span className="inline-flex items-center gap-1 text-felt-700">
            <UserPlus className="size-3" />
            {plan.newPlayers.length} new {plan.newPlayers.length === 1 ? 'player' : 'players'}
          </span>
        ) : null}
        {!plan.hasHeader ? (
          <span className="inline-flex items-center gap-1">
            <Info className="size-3" /> No heading row — columns read in template order
          </span>
        ) : null}
      </div>

      {plan.replacing > 0 ? (
        <p className="shrink-0 rounded-lg border border-gold-500/40 bg-gold-500/10 p-3 text-xs text-ink-900">
          This tournament already has <strong>{plan.replacing}</strong>{' '}
          {plan.replacing === 1 ? 'result' : 'results'}. Importing replaces{' '}
          {plan.replacing === 1 ? 'it' : 'all of them'} with what is below.
        </p>
      ) : null}

      {errors.length > 0 ? (
        <IssueList
          tone="error"
          title={`${errors.length} ${errors.length === 1 ? 'problem stops' : 'problems stop'} this import`}
          issues={errors}
        />
      ) : null}

      {warnings.length > 0 ? (
        <IssueList
          tone="warning"
          title={`${warnings.length} ${warnings.length === 1 ? 'thing' : 'things'} worth knowing`}
          issues={warnings}
        />
      ) : null}

      {plan.results.length > 0 ? (
        // Two levels on purpose, matching the tournament review. The outer section is the bounded
        // box — `overflow-hidden` stops the table from setting the height — and only the inner
        // div scrolls. Putting `overflow-auto` straight onto the flex child instead lets a long
        // table grow past the dialog and clip with no scrollbar, which is what a 392-row file did.
        <section className="flex min-h-40 min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-ink-800/15">
          <div className="min-h-0 flex-1 overflow-auto">
            <table className="w-full border-collapse text-sm">
              <thead className="sticky top-0 z-10 bg-felt-50/80 backdrop-blur">
                <tr className="text-left text-[11px] uppercase tracking-wide text-ink-800/60">
                  <th className="w-14 px-3 py-2 font-medium">Place</th>
                  <th className="min-w-44 px-3 py-2 font-medium">Player</th>
                  <th className="w-32 px-3 py-2 font-medium">Card</th>
                  <th className="w-28 px-3 py-2 text-right font-medium">Prize</th>
                  <th className="w-16 px-3 py-2 text-center font-medium">Buy-ins</th>
                  <th className="w-14 px-3 py-2 text-center font-medium">Deal</th>
                  <th className="min-w-40 px-3 py-2 font-medium">Notes</th>
                </tr>
              </thead>
              <tbody>
                {plan.results.map((r) => (
                  <tr key={r.line} className="border-b border-ink-800/5 last:border-0">
                    <td className="px-3 py-1.5 font-mono font-semibold text-ink-950">
                      {r.place ?? <span className="text-ink-800/35">—</span>}
                    </td>
                    <td className="px-3 py-1.5">
                      <span className="block truncate text-ink-950">{r.displayName}</span>
                      {r.unclaimed !== null ? (
                        // The reason is the operator's check on this row. V1 in particular: a
                        // mistyped card on a row with no name reads "card not at this room".
                        <span className="text-[10px] font-medium text-ink-800/60">{r.unclaimed}</span>
                      ) : r.player === null ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-medium text-felt-700">
                          <UserPlus className="size-2.5" /> will be created
                          {r.city || r.state
                            ? ` · ${[r.city, r.state].filter(Boolean).join(', ')}`
                            : ''}
                        </span>
                      ) : null}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-xs text-ink-800/60">
                      {r.unclaimed !== null ? (
                        // What the file said, never the finisher's card: shown faint, and titled so.
                        <span className="text-ink-800/35" title="What the file carried. It is not the player's card.">
                          {r.sourceCardId ?? '—'}
                        </span>
                      ) : (
                        r.cardId
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono text-ink-950">
                      ${r.prize.toLocaleString()}
                    </td>
                    <td className="px-3 py-1.5 text-center text-ink-800/70">{r.buyins ?? '—'}</td>
                    <td className="px-3 py-1.5 text-center text-ink-800/70">
                      {r.isInDeal ? 'Yes' : '—'}
                    </td>
                    <td className="px-3 py-1.5 text-xs text-ink-800/70">
                      <span className="block truncate" title={r.prizeNotes ?? undefined}>
                        {r.prizeNotes ?? ''}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </div>
  );
}

function IssueList({
  tone,
  title,
  issues,
}: {
  tone: 'error' | 'warning';
  title: string;
  issues: { line?: number; message: string }[];
}) {
  const isError = tone === 'error';
  return (
    <div
      className={`max-h-40 shrink-0 overflow-auto rounded-lg border p-3 ${
        isError ? 'border-red-500/40 bg-red-500/5' : 'border-gold-500/40 bg-gold-500/10'
      }`}
    >
      <p
        className={`mb-1 flex items-center gap-1.5 text-xs font-semibold ${
          isError ? 'text-red-700' : 'text-ink-900'
        }`}
      >
        <AlertTriangle className="size-3.5" /> {title}
      </p>
      <ul className={`space-y-0.5 text-xs ${isError ? 'text-red-700/90' : 'text-ink-900/80'}`}>
        {issues.map((issue, i) => (
          <li key={`${issue.line ?? 'file'}-${i}`}>
            {issue.line !== undefined ? (
              <span className="font-mono text-[11px] opacity-70">Line {issue.line}: </span>
            ) : null}
            {issue.message}
          </li>
        ))}
      </ul>
    </div>
  );
}
